import { Box, Grid, Typography, useScrollTrigger } from "@material-ui/core";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { RouterProps } from "react-router";
import clsx from "clsx";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  B2BButton,
  B2BLoadingPopup,
  BannerSeverity,
  ExperiencesAvailabilityCard,
  FloatingMenuPill,
  formatShortDate,
  Icon,
  IconName,
  NotificationBanner,
  useDeviceTypes,
  Header,
  ActionButton,
  isOlderThan,
  SanitizedHtml,
} from "halifax";
import {
  ExperienceId,
  ACTIVITIES_DATELESS_SELECT_DATES_CLICKED,
  ACTIVITIES_TAG_SELECTED,
  ExperiencesAvailabilitySort,
  ExperienceTagCount,
  VIEWED_ACTIVITY_IN_LIST,
  CallState,
  ActivitiesEntryTypeEnum,
  ACTIVITY_EXIT_SURVEY_SHOWN,
  ACTIVITY_EXIT_SURVEY_OPENED,
  ACTIVITY_EXIT_SURVEY_DISMISSED,
} from "redmond";
import { Skeleton } from "@material-ui/lab";
import {
  CATEGORY_TAG_ID_PARAM,
  CDN_ASSETS,
  getExperiencesShopQuery,
  travelAppInterface,
  TravelAppInterfaceEventName,
  TravelAppInterfaceFunnelType,
  useGetUserMetadataQuery,
  useIsTravelApp,
  useUpdateUserMetadataMutation,
} from "@capone/common";
import { getSelectionId } from "../../utils/selectionHelpers";

import { config } from "../../api/config";
import "./styles.scss";
import * as textConstants from "../../utils/textConstants";
import { RewardsAccountSelection } from "../rewards/components";
import { ExperiencesAvailabilityConnectorProps } from "./container";
import {
  ACTIVITIES_VIEW_TOGGLE,
  CLEAR_ALL_FILTERS,
  DATELESS_BANNER_CTA,
  DATELESS_BANNER_TEXT,
  DATELESS_HEADER_TEXT,
  DESKTOP_DATE_PICKER_SELECTOR,
  FETCH_AVAILABILITY_TEXT,
  getEarnTagText,
  NO_RESULTS_SUBTITLE,
  NO_RESULTS_TITLE,
  SEARCH_TITLE,
  VIEWING_EXPERIENCES_HEADER_TEXT,
} from "./textConstants";
import { ClientContext } from "../../App";
import { PATH_HOME, PATH_SHOP } from "../../utils/paths";
import { AvailabilitySearchControl } from "./components/AvailabilitySearchControl";
import { TravelWalletDrawer } from "../travel-wallet/components";
import { ExperiencesAvailabilityCallState } from "./reducer";
import { getEarnMessageToDisplay } from "../search/textConstants";
import { KeywordSearch } from "./components/KeywordSearch";
import {
  AvailabilitySortDropdown,
  ViewToggle,
} from "./components/AvailabilityFilters";
import { AvailabilityFilterPill } from "./availabilityFilterPillConfig";
import { AvailabilityErrorModal } from "./components/AvailabilityErrorModal";
import { MobileAvailabilitySearchControl } from "./components/MobileAvailabilitySearchControl";
import { BadgeExcellenceModal } from "../common";
import { AvailabilityFilter } from "./components/AvailabilityFilter";
import { MobileAvailabilityFilter } from "./components/MobileAvailabilityFilter";
import { trackEvent } from "../../api/v0/analytics/trackEvent";
import { SEARCH_AGAIN } from "../common/textConstants";
import Logger from "../../utils/logger";
import {
  ACTIVITIES_SURVEY,
  AVAILABLE,
  COMBINED_SEARCH_EXPERIMENT,
  DATELESS_SEARCH_EXPERIMENT,
  getExperimentVariant,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../context/experiments";
import { ActivitiesSurvey } from "../survey";
import { ActivitiesSurveyPrompt } from "./components/SurveyPrompt";
import dayjs from "dayjs";
import { AVAILABILITY_GRID_CONFIG } from "./availabilityFilterPillConfig";

export interface IExperiencesAvailability
  extends ExperiencesAvailabilityConnectorProps,
    RouterProps {}

export const ExperiencesAvailability = (props: IExperiencesAvailability) => {
  const {
    history,
    isReadyToFetchMoreExperiences,
    fromDate,
    untilDate,
    location,
    experiencesAvailabilityCallState,
    largestValueAccount,
    fetchRewardsAccountsCallState,
    fetchInitialExperiencesAvailability,
    fetchMoreExperiencesAvailability,
    fetchFilteredExperiencesAvailability,
    getAvailabilityRequestParameters,
    experiencesMap,
    availabilityFilterPills,
    rewardsKey,
    filtersChangedSinceLastSearch,
    priceFiltersChangedSinceLastSearch,
    sortOption,
    setSortOption,
    setFilterTags,
    fetchTravelWalletDetails,
    fetchTravelWalletDetailsCallState,
    activitiesAvailabilityProperties,
    activitiesDetailsProperties,
    resetFilterAndSort,
    resetExperiencesAvailability,
    selectedGroupedExperience,
    setSelectedGroupedExperience,
    setCategoryTagId,
    availabilityNextPageToken,
    selectedRewardsAccount,
    entryType = ActivitiesEntryTypeEnum.HOMEPAGE,
    lodgingId,
    hasBookedActivities,
    fromActivityShopPage,
    fetchBookedActivities,
    viewPreference,
    setViewPreference,
  } = props;

  const {PAGE_SIZE} = AVAILABILITY_GRID_CONFIG;
  const [experiencesToShow, setExperiencesToShow] = useState<ExperienceId[]>();

  const { isTravelApp } = useIsTravelApp();

  const {
    data: userPreferenceMetadata,
    isLoading: userPreferencesMetadataLoading,
  } = useGetUserMetadataQuery({
    keys: [
      "hasAnsweredActivitiesSurvey",
      "lastSeenActivitiesSurvey",
      "userHasVisitedActivityShopPage",
    ],
  });

  const [updateUserMetadata] = useUpdateUserMetadataMutation();
  const [mobileCalendarOpen, setMobileCalendarOpen] = useState(false);

  useEffect(() => {
    if (
      experiencesMap &&
      (experiencesAvailabilityCallState ===
        ExperiencesAvailabilityCallState.InitialSearchCallSuccess ||
        experiencesAvailabilityCallState ===
          ExperiencesAvailabilityCallState.Complete)
    ) {
      let newExperiencesToShow = Array.from(experiencesMap.values()).map(
        (obj) => obj.id,
      );
      // For filtered views (attraction/category), show all activities when complete.
      // For "Most Popular" view, truncate to PAGE_SIZE for frontend pagination.
      const isFilteredView = selectedGroupedExperience !== undefined;
      const isComplete =
        experiencesAvailabilityCallState ===
        ExperiencesAvailabilityCallState.Complete;

      if (!isFilteredView || !isComplete) {
        newExperiencesToShow = newExperiencesToShow.slice(0, PAGE_SIZE);
      }
      setExperiencesToShow(newExperiencesToShow);
    }
  }, [experiencesAvailabilityCallState, selectedGroupedExperience]);

  const { logo, showCoreHomepage } = useContext(ClientContext);
  const [isBadgeExcellenceModalOpen, setBadgeExcellenceModalOpen] =
    useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const [isSurveyModalOpen, setIsSurveyModalOpen] = useState(false);

  const { matchesMobile, matchesSmallDesktop, matchesTablet } = useDeviceTypes({
    desktopMin: 961,
    mobileMax: 960,
    smallDesktopMin: 961,
    smallDesktopMax: 1280,
    largeDesktopMin: 1280,
    tabletMin: 768,
    tabletMax: 960,
  });

  const expState = useExperiments();
  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const isActivitiesSurvey =
    getExperimentVariant(expState.experiments, ACTIVITIES_SURVEY) === AVAILABLE;

  const isDatelessSearchEnabled =
    getExperimentVariant(expState.experiments, DATELESS_SEARCH_EXPERIMENT) ===
    AVAILABLE;

  const isCombinedSearchEnabled =
    getExperimentVariant(expState.experiments, COMBINED_SEARCH_EXPERIMENT) ===
    AVAILABLE;

  const isDateless = isDatelessSearchEnabled && !fromDate && !untilDate;

  const scrollTrigger = useScrollTrigger({ disableHysteresis: true });

  const initialCallInProgress =
    experiencesAvailabilityCallState ===
    ExperiencesAvailabilityCallState.InitialSearchCallInProcess;

  const shouldRenderSkeleton =
    experiencesAvailabilityCallState ===
      ExperiencesAvailabilityCallState.NotCalled ||
    experiencesAvailabilityCallState ===
      ExperiencesAvailabilityCallState.InitialSearchCallInProcess ||
    experiencesAvailabilityCallState ===
      ExperiencesAvailabilityCallState.InitialFilteredSearchCallInProcess;

  // Pills only need skeletons on initial load or destination change, not when switching pills
  const shouldRenderPillSkeleton =
    experiencesAvailabilityCallState ===
      ExperiencesAvailabilityCallState.NotCalled ||
    experiencesAvailabilityCallState ===
      ExperiencesAvailabilityCallState.InitialSearchCallInProcess;

  const numberOfQuickFilterSkeletons = 5;

  // For the selected pill, show the actual resolved activity count instead of
  // the backend's pre-computed tag count, which can differ due to product
  // resolution filtering. Only update when all pages are loaded (Complete)
  // to avoid count flickering during pagination.
  const stablePillCountRef = useRef<Map<number, number>>(new Map());
  const getPillDisplayCount = (pill: AvailabilityFilterPill): number => {
    const isSelected = selectedGroupedExperience?.tag?.id === pill.tagCount.tag.id;
    const isComplete =
      experiencesAvailabilityCallState === ExperiencesAvailabilityCallState.Complete;

    if (isSelected && isComplete && experiencesMap.size > 0) {
      // All pages loaded — use the definitive count
      stablePillCountRef.current.set(pill.tagCount.tag.id, experiencesMap.size);
      return experiencesMap.size;
    }

    if (isSelected) {
      // Selected but still loading — hold the last stable count to avoid flickering
      return stablePillCountRef.current.get(pill.tagCount.tag.id) ?? pill.tagCount.count;
    }

    // Unselected pill — use backend count
    return pill.tagCount.count;
  };

  useEffect(() => {
    fetchBookedActivities();
  }, []);

  // criteria for being able show show survey prompt
  const showSurvey = useMemo(() => {
    return (
      isActivitiesSurvey &&
      !isTravelApp &&
      !hasBookedActivities &&
      !userPreferencesMetadataLoading &&
      !userPreferenceMetadata?.userMetadata?.hasAnsweredActivitiesSurvey &&
      (!userPreferenceMetadata?.userMetadata?.lastSeenActivitiesSurvey ||
        isOlderThan(
          new Date(
            userPreferenceMetadata?.userMetadata?.lastSeenActivitiesSurvey,
          ),
          14,
        ))
    );
  }, [
    isActivitiesSurvey,
    isTravelApp,
    hasBookedActivities,
    userPreferencesMetadataLoading,
    userPreferenceMetadata,
  ]);

  const [isSurveyPromptOpen, setIsSurveyPromptOpen] = useState(
    (showSurvey && fromActivityShopPage) || false,
  );

  useEffect(() => {
    const userVisitedShopPage =
      userPreferenceMetadata?.userMetadata?.userHasVisitedActivityShopPage;
    setIsSurveyPromptOpen(
      (showSurvey && (userVisitedShopPage || fromActivityShopPage)) || false,
    );
  }, [showSurvey, fromActivityShopPage, userPreferenceMetadata]);

  useEffect(() => {
    if (isSurveyPromptOpen) {
      updateUserMetadata({
        userMetadata: {
          lastSeenActivitiesSurvey: dayjs().toISOString(),
        },
      });
      trackEvent({
        eventName: ACTIVITY_EXIT_SURVEY_SHOWN,
        properties: {
          ...activitiesAvailabilityProperties.properties,
          ...activitiesDetailsProperties.properties,
        },
        encryptedProperties: [
          ...activitiesAvailabilityProperties.encryptedProperties,
          ...activitiesDetailsProperties.encryptedProperties,
        ],
      });
    }
  }, [isSurveyPromptOpen]);

  // Only get travel wallet details if they don't already get populated from the search page
  useEffect(() => {
    setTimeout(() => window.scrollTo(0, 0), 0);

    // Reset availability state on mount so the gated effect below
    // reliably sees `NotCalled` and dispatches the initial fetch. The
    // prior cleanup-on-unmount-only reset was racy in practice:
    // navigating /shop → /availability via "Back to results" could
    // leave the previous callState intact, suppress the gated
    // dispatch, and render "No activities found" without ever hitting
    // the API. The gated effect (line ~370) handles the actual fetch
    // — don't dispatch here too or two requests can race.
    resetExperiencesAvailability();

    if (
      fetchTravelWalletDetailsCallState !== CallState.Success &&
      fetchTravelWalletDetailsCallState !== CallState.InProcess
    ) {
      fetchTravelWalletDetails();
    }

    return () => {
      resetExperiencesAvailability();
    };
  }, []); // Empty dependency array means this runs on mount and cleanup on unmount

  // Reset selection when location changes (e.g., user searches for a new city)
  // Use location?.label instead of the location object to avoid resetting on
  // object reference changes when the actual location hasn't changed
  useEffect(() => {
    setSelectedGroupedExperience(undefined);
  }, [location?.label]);

  useEffect(() => {
    if (
      experiencesAvailabilityCallState ===
      ExperiencesAvailabilityCallState.NotCalled
    ) {
      getAvailabilityRequestParameters(history, isDatelessSearchEnabled);
      fetchInitialExperiencesAvailability(history, isDatelessSearchEnabled);
    } else if (filtersChangedSinceLastSearch) {
      fetchFilteredExperiencesAvailability(history, false, isDatelessSearchEnabled);
    } else if (priceFiltersChangedSinceLastSearch) {
      setTimeout(() => {
        fetchFilteredExperiencesAvailability(history, false, isDatelessSearchEnabled);
      }, 500);
    }
  }, [
    experiencesAvailabilityCallState,
    filtersChangedSinceLastSearch,
    priceFiltersChangedSinceLastSearch,
    isDatelessSearchEnabled,
  ]);

  useEffect(() => {
    if (isReadyToFetchMoreExperiences) {
      setTimeout(() => {
        fetchMoreExperiencesAvailability(history, isDatelessSearchEnabled);
      }, 500);
    }
  }, [
    isReadyToFetchMoreExperiences,
    fetchMoreExperiencesAvailability,
    history,
  ]);

  const setFetchMoreDataMostPopular = () => {
    const newPageSize = (experiencesToShow?.length ?? 0) + PAGE_SIZE;

    if (experiencesMap) {
      return setExperiencesToShow(
        Array.from(experiencesMap.values())
          .map((obj) => obj.id)
          .slice(0, newPageSize),
      );
    }
    return null;
  };

  const setFetchMoreData = () => {
    if (selectedGroupedExperience && experiencesMap) {
      return setExperiencesToShow(
        Array.from(experiencesMap.values()).map((obj) => obj.id),
      );
    }
    return null;
  };

  const headerDateText =
    fromDate && untilDate ? (
      <Typography variant="body2">
        {textConstants.DATES_TEXT(
          formatShortDate(fromDate),
          formatShortDate(untilDate),
        )}
      </Typography>
    ) : null;

  const handleQuickFilterChange = (
    option: ExperienceTagCount,
    placement: "tag-pill" | "view-all-cta" | "availability-search",
    availabilityFilterType?: "Attraction" | "Category",
  ) => {
    const searchParams = new URLSearchParams(history.location.search);

    if (selectedGroupedExperience?.tag?.id === option.tag.id) {
      setSelectedGroupedExperience(undefined);
      setFilterTags([]);

      setCategoryTagId(null);
      searchParams.delete(CATEGORY_TAG_ID_PARAM);
    } else {
      const { properties, encryptedProperties } =
        activitiesAvailabilityProperties;
      trackEvent({
        eventName: ACTIVITIES_TAG_SELECTED,
        properties: {
          ...properties,
          entry_type: placement,
          entry_types_all: [placement, ...(properties.entry_types_all ?? [])],
          tag_selected: option.tag.name,
          tag_type: availabilityFilterType ?? "Category",
        },
        encryptedProperties,
      });
      setSelectedGroupedExperience(option);

      const kind = availabilityFilterType ?? "Category";
      setFilterTags([{ ...option.tag, kind: { ActivityTagKind: kind } }]);

      const kindSuffix = kind === "Attraction" ? "_1" : "_0";
      setCategoryTagId(option.tag.id, kind);
      searchParams.set(CATEGORY_TAG_ID_PARAM, `${option.tag.id}${kindSuffix}`);
    }

    // Update URL without refresh
    history.replace({
      pathname: history.location.pathname,
      search: searchParams.toString(),
    });

    setExperiencesToShow([]);
  };

  const handleViewToggle = (newView: "grid" | "list") => {
    const { properties, encryptedProperties } = activitiesAvailabilityProperties;
    trackEvent({
      eventName: ACTIVITIES_VIEW_TOGGLE,
      properties: {
        ...properties,
        new_view_selected: newView,
      },
      encryptedProperties,
    });
    setViewPreference(newView);
  };

  const renderNoResults = () => (
    <Box className="experiences-no-results-container">
      <Icon name={IconName.ErrorState} />
      <Typography className="experiences-no-results-title" variant="h2">
        {NO_RESULTS_TITLE}
      </Typography>
      <Typography className="experiences-no-results-subtitle">
        {NO_RESULTS_SUBTITLE}
      </Typography>
      <Box className="experiences-no-results-buttons-container">
        <ActionButton
          onClick={() => {
            resetFilterAndSort();
            setCategoryTagId(null);
            const searchParams = new URLSearchParams(history.location.search);
            searchParams.delete(CATEGORY_TAG_ID_PARAM);
            history.replace({
              pathname: history.location.pathname,
              search: searchParams.toString(),
            });
            // Explicitly fetch with resetPriceFilterConstraints=true to
            // restore price range from the unfiltered response (a zero-result
            // filter may have set prices to undefined).
            fetchFilteredExperiencesAvailability(history, true);
          }}
          message={CLEAR_ALL_FILTERS}
          defaultStyle="h4r-primary"
        />
        {!matchesMobile && (
          <ActionButton
            onClick={() => fetchFilteredExperiencesAvailability(history, true)}
            message={SEARCH_AGAIN}
            defaultStyle="h4r-secondary"
          />
        )}
      </Box>
    </Box>
  );

  const renderMostPopularPageSkeleton = () => {
    const useGridView = viewPreference === "grid" && !matchesMobile;

    return (
      <>
        {!isCombinedSearchEnabled && (
          <Box className={clsx("most-popular-title-section")}>
            <KeywordSearch
              title={SEARCH_TITLE(location?.label)}
              setSelectedGroupedExperience={(option) => {
                handleQuickFilterChange(option, "availability-search");
              }}
              isMobile={matchesMobile}
              location={location}
            />
          </Box>
        )}
        <Box className={clsx("most-popular-subtitle-section", { mobile: matchesMobile })}>
          <Typography
            variant="h3"
            className={clsx("most-popular-subtitle", {
              xmod: isXmodRedesign,
            })}
          >
            {"Most Popular"}
          </Typography>
          {!matchesMobile && (
            <Box className="sort-and-view-controls">
              <AvailabilitySortDropdown
                availabilitySortOption={sortOption}
                setAvailabilitySortOption={setSortOption}
              />
              <ViewToggle
                viewPreference={viewPreference}
                onViewChange={handleViewToggle}
              />
            </Box>
          )}
        </Box>
        {useGridView ? (
          <Grid container spacing={4}>
            {[...Array(24)].map((_, index) => {
              return (
                <Grid item xs={12} md={6} lg={3} key={index}>
                  <ExperiencesAvailabilityCard
                    isMobile={matchesMobile}
                    variant={"card"}
                    isSkeleton={true}
                    isXmodRedesign={isXmodRedesign}
                    isDesignV2={true}
                  />
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Box className="experiences-category-row-container most-popular-list">
            {[...Array(5)].map((_, index) => (
              <Box className="loading-experiences" key={index}>
                <ExperiencesAvailabilityCard
                  isMobile={matchesMobile}
                  variant={matchesMobile ? "card" : "row"}
                  isSkeleton={true}
                  isXmodRedesign={isXmodRedesign}
                  isDesignV2={true}
                />
              </Box>
            ))}
          </Box>
        )}
      </>
    );
  };

  const renderMostPopularPageSection = () =>
    !initialCallInProgress &&
    !shouldRenderSkeleton &&
    experiencesMap.size === 0 ? (
      renderNoResults()
    ) : (
      <>
        {!isCombinedSearchEnabled && (
          <Box className={clsx("most-popular-title-section")}>
            <KeywordSearch
              title={SEARCH_TITLE(location?.label)}
              setSelectedGroupedExperience={(option) => {
                handleQuickFilterChange(option, "availability-search");
              }}
              isMobile={matchesMobile}
              location={location}
            />
          </Box>
        )}
        <Box className={clsx("most-popular-subtitle-section", { mobile: matchesMobile })}>
          <Typography
            variant="h3"
            className={clsx("most-popular-subtitle", {
              xmod: isXmodRedesign,
            })}
          >
            {"Most Popular"}
          </Typography>
          {!matchesMobile && (
            <Box className="sort-and-view-controls">
              <AvailabilitySortDropdown
                availabilitySortOption={sortOption}
                setAvailabilitySortOption={setSortOption}
              />
              <ViewToggle
                viewPreference={viewPreference}
                onViewChange={handleViewToggle}
              />
            </Box>
          )}
        </Box>
        {experiencesToShow && experiencesToShow.length > 0 && !shouldRenderSkeleton && (
          <Box
            className={clsx({
              "experiences-most-popular-container": viewPreference === "grid",
              "experiences-category-row-container most-popular-list": viewPreference === "list",
              mobile: matchesMobile,
              tablet: matchesSmallDesktop || matchesTablet,
            })}
          >
            <InfiniteScroll
              dataLength={experiencesToShow.length}
              hasChildren={Boolean(experiencesToShow.length)}
              hasMore={experiencesMap.size > experiencesToShow.length}
              loader={renderSkeletonRow()}
              next={setFetchMoreDataMostPopular}
            >
              {viewPreference === "grid" && !matchesMobile ? (
                <Grid container spacing={matchesMobile ? 2 : 4}>
                  {experiencesToShow.map((experienceId, index) => {
                    const experienceAvailabilityInfo = experiencesMap.get(
                      experienceId.value,
                    );

                    if (experienceAvailabilityInfo) {
                      return (
                        <Grid item xs={12} md={6} lg={3} key={index}>
                          <ExperiencesAvailabilityCard
                            isDesignV2={true}
                            trackEvent={() => {
                              trackEvent({
                                eventName: VIEWED_ACTIVITY_IN_LIST,
                                properties: {
                                  ...activitiesAvailabilityProperties.properties,
                                  productId: experienceId.value,
                                  tag_selected:
                                    selectedGroupedExperience?.tag.name,
                                  price:
                                    experienceAvailabilityInfo.bestPrice.fiat
                                      .value,
                                  sort_index: index,
                                  sort_used: sortOption,
                                  activity_name: experienceAvailabilityInfo.name,
                                },
                                encryptedProperties:
                                  activitiesAvailabilityProperties.encryptedProperties,
                              });
                            }}
                            onClick={() => {
                              // setting into local storage the shop context for clicked experience
                              // to be used in the shop page
                              try {
                                localStorage.setItem(
                                  `shop-context-${experienceId.value}`,
                                  JSON.stringify({
                                    destinationId: location?.id
                                      ? getSelectionId(location.id)
                                      : "",
                                    entryTag: selectedGroupedExperience?.tag,
                                    searchLocation: location?.label,
                                    availabilityPricingOpaque:
                                      experienceAvailabilityInfo.pricingOpaque,
                                    thumbnailUrl:
                                      experienceAvailabilityInfo.coverImage,
                                  }),
                                );
                              } catch (e) {
                                Logger.debug(e);
                              }
                              window.open(
                                `${PATH_SHOP}${getExperiencesShopQuery(
                                  experienceId,
                                  fromDate,
                                  untilDate,
                                  undefined,
                                  isTravelApp,
                                  index,
                                  entryType,
                                  activitiesAvailabilityProperties.properties
                                    .entry_types_all ?? [],
                                  lodgingId ?? undefined,
                                )}`,
                                "_blank",
                              );
                              if (showSurvey) {
                                setIsSurveyPromptOpen(true);
                              }
                            }}
                            experiencesAvailabilityInfo={
                              experienceAvailabilityInfo
                            }
                            isMobile={matchesMobile}
                            variant={"card"}
                            rewardsKey={rewardsKey}
                            onBadgeExcellenceTagClick={() => {
                              if (!isBadgeExcellenceModalOpen)
                                setBadgeExcellenceModalOpen(true);
                            }}
                            earnTagContent={
                              selectedRewardsAccount && (
                                <>
                                  <Icon name={IconName.StarIcon} />
                                  <SanitizedHtml
                                    as={Typography}
                                    className="earn-tag-text"
                                    html={getEarnTagText(
                                        selectedRewardsAccount.earn
                                          .experiencesMultiplier,
                                        selectedRewardsAccount.rewardsBalance
                                          .currencyDescription ??
                                          selectedRewardsAccount.rewardsBalance
                                            .currency,
                                      )}
                                  />
                                </>
                              )
                            }
                            isSkeleton={shouldRenderSkeleton}
                            isXmodRedesign={isXmodRedesign}
                            isDateless={isDateless}
                          />
                        </Grid>
                      );
                    }
                    return null;
                  })}
                </Grid>
              ) : (
                // List view or mobile view
                experiencesToShow.map((experienceId, index) => {
                  const experienceAvailabilityInfo = experiencesMap.get(
                    experienceId.value,
                  );

                  if (experienceAvailabilityInfo) {
                    return (
                      <ExperiencesAvailabilityCard
                        key={index}
                        isDesignV2={true}
                        descriptionLength={
                          matchesTablet || matchesSmallDesktop ? 105 : undefined
                        }
                        trackEvent={() => {
                          trackEvent({
                            eventName: VIEWED_ACTIVITY_IN_LIST,
                            properties: {
                              ...activitiesAvailabilityProperties.properties,
                              productId: experienceId.value,
                              tag_selected:
                                selectedGroupedExperience?.tag.name,
                              price:
                                experienceAvailabilityInfo.bestPrice.fiat.value,
                              sort_index: index,
                              sort_used: sortOption,
                              activity_name: experienceAvailabilityInfo.name,
                            },
                            encryptedProperties:
                              activitiesAvailabilityProperties.encryptedProperties,
                          });
                        }}
                        onClick={() => {
                          try {
                            localStorage.setItem(
                              `shop-context-${experienceId.value}`,
                              JSON.stringify({
                                destinationId: location?.id
                                  ? getSelectionId(location.id)
                                  : "",
                                entryTag: selectedGroupedExperience?.tag,
                                searchLocation: location?.label,
                                availabilityPricingOpaque:
                                  experienceAvailabilityInfo.pricingOpaque,
                              }),
                            );
                          } catch (e) {
                            Logger.debug(e);
                          }
                          if (matchesMobile) {
                            history.push(
                              `${PATH_SHOP}${getExperiencesShopQuery(
                                experienceId,
                                fromDate,
                                untilDate,
                                undefined,
                                isTravelApp,
                                index,
                                entryType,
                                activitiesAvailabilityProperties.properties
                                  .entry_types_all ?? [],
                                lodgingId ?? undefined,
                              )}`,
                              { fromPage: window.location.pathname },
                            );
                            window.scrollTo(0, 0);
                          } else {
                            window.open(
                              `${PATH_SHOP}${getExperiencesShopQuery(
                                experienceId,
                                fromDate,
                                untilDate,
                                undefined,
                                isTravelApp,
                                index,
                                entryType,
                                activitiesAvailabilityProperties.properties
                                  .entry_types_all ?? [],
                                lodgingId ?? undefined,
                              )}`,
                              "_blank",
                            );
                          }
                          if (showSurvey) {
                            setIsSurveyPromptOpen(true);
                          }
                        }}
                        experiencesAvailabilityInfo={experienceAvailabilityInfo}
                        isMobile={matchesMobile}
                        variant={!matchesMobile ? "row" : "card"}
                        rewardsKey={rewardsKey}
                        onBadgeExcellenceTagClick={() => {
                          if (!isBadgeExcellenceModalOpen)
                            setBadgeExcellenceModalOpen(true);
                        }}
                        earnTagContent={
                          selectedRewardsAccount && (
                            <>
                              <Icon name={IconName.StarIcon} />
                              <SanitizedHtml
                                as={Typography}
                                className="earn-tag-text"
                                html={getEarnTagText(
                                    selectedRewardsAccount.earn
                                      .experiencesMultiplier,
                                    selectedRewardsAccount.rewardsBalance
                                      .currencyDescription ??
                                      selectedRewardsAccount.rewardsBalance
                                        .currency,
                                  )}
                              />
                            </>
                          )
                        }
                        isSkeleton={shouldRenderSkeleton}
                        isXmodRedesign={isXmodRedesign}
                        isDateless={isDateless}
                      />
                    );
                  }
                  return null;
                })
              )}
            </InfiniteScroll>
          </Box>
        )}
        {showSurvey ? (
          <React.Fragment>
            <ActivitiesSurveyPrompt
              isMobile={matchesMobile}
              isOpen={isSurveyPromptOpen}
              setOpen={setIsSurveyPromptOpen}
              yesButton={() => {
                updateUserMetadata({
                  userMetadata: {
                    hasAnsweredActivitiesSurvey: true,
                  },
                });
                setIsSurveyModalOpen(true);
                setIsSurveyPromptOpen(false);
                trackEvent({
                  eventName: ACTIVITY_EXIT_SURVEY_OPENED,
                  properties: {
                    ...activitiesAvailabilityProperties.properties,
                    ...activitiesDetailsProperties.properties,
                  },
                  encryptedProperties: [
                    ...activitiesAvailabilityProperties.encryptedProperties,
                    ...activitiesDetailsProperties.encryptedProperties,
                  ],
                });
              }}
              noButton={() => {
                updateUserMetadata({
                  userMetadata: {
                    lastSeenActivitiesSurvey: dayjs().toISOString(),
                  },
                });
                setIsSurveyPromptOpen(false);
                trackEvent({
                  eventName: ACTIVITY_EXIT_SURVEY_DISMISSED,
                  properties: {
                    ...activitiesAvailabilityProperties.properties,
                    ...activitiesDetailsProperties.properties,
                  },
                  encryptedProperties: [
                    ...activitiesAvailabilityProperties.encryptedProperties,
                    ...activitiesDetailsProperties.encryptedProperties,
                  ],
                });
              }}
            />
            <ActivitiesSurvey
              isOpen={isSurveyModalOpen}
              setOpen={setIsSurveyModalOpen}
              isMobile={matchesMobile}
              trackingProperties={{
                ...activitiesAvailabilityProperties.properties,
                ...activitiesDetailsProperties.properties,
              }}
              encryptedProperties={[
                ...activitiesAvailabilityProperties.encryptedProperties,
                ...activitiesDetailsProperties.encryptedProperties,
              ]}
            />
          </React.Fragment>
        ) : undefined}
      </>
    );

  const renderSkeletonRow = (cardCount: number = 4) => {
    const useGridView = viewPreference === "grid" && !matchesMobile;
    const cardVariant = useGridView || matchesMobile ? "card" : "row";

    if (useGridView) {
      return (
        <Grid container spacing={4} className="loading-experiences">
          {[...Array(cardCount)].map((_, index) => (
            <Grid item xs={12} md={6} lg={3} key={`loader-skeleton-${index}`}>
              <ExperiencesAvailabilityCard
                variant="card"
                isMobile={matchesMobile}
                isSkeleton
                isXmodRedesign={isXmodRedesign}
                isDesignV2={true}
              />
            </Grid>
          ))}
        </Grid>
      );
    }

    return (
      <Box className="loading-experiences">
        <ExperiencesAvailabilityCard
          variant={cardVariant}
          isMobile={matchesMobile}
          isSkeleton
          isXmodRedesign={isXmodRedesign}
        />
      </Box>
    );
  };

  const renderFilteredSkeleton = () => {
    const useGridView = viewPreference === "grid" && !matchesMobile;
    const cardVariant = useGridView || matchesMobile ? "card" : "row";

    if (useGridView) {
      return (
        <Box
          className={clsx("experiences-most-popular-container", {
            mobile: matchesMobile,
          })}
        >
          <Grid container spacing={matchesMobile ? 2 : 4}>
            {[...Array(8)].map((_, index) => (
              <Grid item xs={12} md={6} lg={3} key={index}>
                <ExperiencesAvailabilityCard
                  variant="card"
                  isMobile={matchesMobile}
                  isSkeleton
                  isXmodRedesign={isXmodRedesign}
                  isDesignV2={true}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      );
    }

    return (
      <Box
        className={clsx("experiences-category-row-container", "filtered", {
          mobile: matchesMobile,
        })}
      >
        {[...Array(5)].map((_, index) => (
          <Box className="loading-experiences" key={index}>
            <ExperiencesAvailabilityCard
              variant={cardVariant}
              isMobile={matchesMobile}
              isSkeleton
              isXmodRedesign={isXmodRedesign}
            />
          </Box>
        ))}
      </Box>
    );
  };

  const renderFilteredRightSection = () =>
    !initialCallInProgress &&
    !shouldRenderSkeleton &&
    experiencesMap.size === 0 ? (
      renderNoResults()
    ) : (
      <>
        <Box className="filtered-title-section">
          <KeywordSearch
            title={`${selectedGroupedExperience?.tag.name}`}
            hideSearchBar
            setSelectedGroupedExperience={setSelectedGroupedExperience}
            isMobile={matchesMobile}
            location={location}
          />
          {!matchesMobile && (
            <Box className="sort-and-view-controls">
              <AvailabilitySortDropdown
                availabilitySortOption={sortOption}
                setAvailabilitySortOption={setSortOption}
              />
              <ViewToggle
                viewPreference={viewPreference}
                onViewChange={handleViewToggle}
              />
            </Box>
          )}
        </Box>
        {(shouldRenderSkeleton ||
          (experiencesMap.size > 0 && !experiencesToShow?.length)) &&
          renderFilteredSkeleton()}
        {experiencesToShow && experiencesToShow.length > 0 && !shouldRenderSkeleton && (
          <Box
            className={clsx({
              "experiences-category-row-container filtered": viewPreference === "list",
              "experiences-most-popular-container": viewPreference === "grid",
              mobile: matchesMobile,
              tablet: matchesSmallDesktop || matchesTablet,
            })}
          >
            <InfiniteScroll
              dataLength={experiencesToShow.length}
              hasMore={
                selectedGroupedExperience
                  ? availabilityNextPageToken !== undefined
                  : false
              }
              loader={renderSkeletonRow()}
              next={setFetchMoreData}
            >
              {viewPreference === "grid" && !matchesMobile ? (
                <Grid container spacing={matchesMobile ? 2 : 4}>
                  {experiencesToShow.map((experienceId, index) => {
                    const experienceAvailabilityInfo = experiencesMap.get(
                      experienceId.value,
                    );

                    if (experienceAvailabilityInfo) {
                      return (
                        <Grid item xs={12} md={6} lg={3} key={index}>
                          <ExperiencesAvailabilityCard
                            isDesignV2={true}
                            trackEvent={() => {
                              trackEvent({
                                eventName: VIEWED_ACTIVITY_IN_LIST,
                                properties: {
                                  ...activitiesAvailabilityProperties.properties,
                                  productId: experienceId.value,
                                  tag_selected: selectedGroupedExperience?.tag.name,
                                  price:
                                    experienceAvailabilityInfo.bestPrice.fiat.value,
                                  sort_index: index,
                                  sort_used: sortOption,
                                  activity_name: experienceAvailabilityInfo.name,
                                },
                                encryptedProperties:
                                  activitiesAvailabilityProperties.encryptedProperties,
                              });
                            }}
                            onClick={() => {
                              // setting into local storage the shop context for clicked experience
                              // to be used in the shop page
                              try {
                                localStorage.setItem(
                                  `shop-context-${experienceId.value}`,
                                  JSON.stringify({
                                    destinationId: location?.id
                                      ? getSelectionId(location.id)
                                      : "",
                                    entryTag: selectedGroupedExperience?.tag,
                                    searchLocation: location?.label,
                                    availabilityPricingOpaque:
                                      experienceAvailabilityInfo.pricingOpaque,
                                    thumbnailUrl:
                                      experienceAvailabilityInfo.coverImage,
                                  }),
                                );
                              } catch (e) {
                                Logger.debug(e);
                              }
                              window.open(
                                `${PATH_SHOP}${getExperiencesShopQuery(
                                  experienceId,
                                  fromDate,
                                  untilDate,
                                  undefined,
                                  isTravelApp,
                                  index,
                                  entryType,
                                  activitiesAvailabilityProperties.properties
                                    .entry_types_all ?? [],
                                  lodgingId ?? undefined,
                                )}`,
                                "_blank",
                              );
                              if (showSurvey) {
                                setIsSurveyPromptOpen(true);
                              }
                            }}
                            experiencesAvailabilityInfo={experienceAvailabilityInfo}
                            isMobile={matchesMobile}
                            variant="card"
                            rewardsKey={rewardsKey}
                            onBadgeExcellenceTagClick={() => {
                              if (!isBadgeExcellenceModalOpen)
                                setBadgeExcellenceModalOpen(true);
                            }}
                            earnTagContent={
                              selectedRewardsAccount && (
                                <>
                                  <Icon name={IconName.StarIcon} />
                                  <SanitizedHtml
                                    as={Typography}
                                    className="earn-tag-text"
                                    html={getEarnTagText(
                                        selectedRewardsAccount.earn
                                          .experiencesMultiplier,
                                        selectedRewardsAccount.rewardsBalance
                                          .currencyDescription ??
                                          selectedRewardsAccount.rewardsBalance
                                            .currency,
                                      )}
                                  />
                                </>
                              )
                            }
                            isSkeleton={shouldRenderSkeleton}
                            isXmodRedesign={isXmodRedesign}
                            isDateless={isDateless}
                          />
                        </Grid>
                      );
                    }
                    return null;
                  })}
                </Grid>
              ) : (
                experiencesToShow.map((experienceId, index) => {
                  const experienceAvailabilityInfo = experiencesMap.get(
                    experienceId.value,
                  );

                  if (experienceAvailabilityInfo) {
                    return (
                      <ExperiencesAvailabilityCard
                        key={index}
                        descriptionLength={
                          matchesTablet || matchesSmallDesktop ? 105 : undefined
                        }
                        trackEvent={() => {
                          trackEvent({
                            eventName: VIEWED_ACTIVITY_IN_LIST,
                            properties: {
                              ...activitiesAvailabilityProperties.properties,
                              productId: experienceId.value,
                              tag_selected: selectedGroupedExperience?.tag.name,
                              price:
                                experienceAvailabilityInfo.bestPrice.fiat.value,
                              sort_index: index,
                              sort_used: sortOption,
                              activity_name: experienceAvailabilityInfo.name,
                            },
                            encryptedProperties:
                              activitiesAvailabilityProperties.encryptedProperties,
                          });
                        }}
                        onClick={() => {
                          try {
                            localStorage.setItem(
                              `shop-context-${experienceId.value}`,
                              JSON.stringify({
                                destinationId: location?.id
                                  ? getSelectionId(location.id)
                                  : "",
                                entryTag: selectedGroupedExperience?.tag,
                                searchLocation: location?.label,
                                availabilityPricingOpaque:
                                  experienceAvailabilityInfo.pricingOpaque,
                              }),
                            );
                          } catch (e) {
                            Logger.debug(e);
                          }

                          if (matchesMobile) {
                            history.push(
                              `${PATH_SHOP}${getExperiencesShopQuery(
                                experienceId,
                                fromDate,
                                untilDate,
                                undefined,
                                isTravelApp,
                                index,
                                entryType,
                                activitiesAvailabilityProperties.properties
                                  .entry_types_all ?? [],
                                lodgingId ?? undefined,
                              )}`,
                            );
                            window.scrollTo(0, 0);
                          } else {
                            window.open(
                              `${PATH_SHOP}${getExperiencesShopQuery(
                                experienceId,
                                fromDate,
                                untilDate,
                                undefined,
                                isTravelApp,
                                index,
                                entryType,
                                activitiesAvailabilityProperties.properties
                                  .entry_types_all ?? [],
                                lodgingId ?? undefined,
                              )}`,
                              "_blank",
                            );
                          }

                          if (showSurvey) {
                            setIsSurveyPromptOpen(true);
                          }
                        }}
                        experiencesAvailabilityInfo={experienceAvailabilityInfo}
                        isMobile={matchesMobile}
                        variant={!matchesMobile ? "row" : "card"}
                        rewardsKey={rewardsKey}
                        onBadgeExcellenceTagClick={() => {
                          if (!isBadgeExcellenceModalOpen)
                            setBadgeExcellenceModalOpen(true);
                        }}
                        earnTagContent={
                          selectedRewardsAccount && (
                            <>
                              <Icon name={IconName.StarIcon} />
                              <SanitizedHtml
                                as={Typography}
                                className="earn-tag-text"
                                html={getEarnTagText(
                                    selectedRewardsAccount.earn
                                      .experiencesMultiplier,
                                    selectedRewardsAccount.rewardsBalance
                                      .currencyDescription ??
                                      selectedRewardsAccount.rewardsBalance
                                        .currency,
                                  )}
                              />
                            </>
                          )
                        }
                        isSkeleton={shouldRenderSkeleton}
                        isXmodRedesign={isXmodRedesign}
                        isDateless={isDateless}
                      />
                    );
                  }

                  return null;
                })
              )}
            </InfiniteScroll>
          </Box>
        )}
      </>
    );

  const renderQuickFilterSkeleton = () =>
    Array.from({ length: numberOfQuickFilterSkeletons }, (_, i) => (
      <B2BButton
        key={`skeleton-filter-${i}`}
        aria-label="Skeleton"
        className={clsx("experiences-availability-quick-filter-button", {
          "category-selected": false,
        })}
        variant="b2b-shop-filter"
      >
        <Box className="generic-dropdown-content-container">
          <Skeleton
            className={clsx(
              "experiences-availability-quick-filter-button",
              "skeleton",
            )}
          />
        </Box>
      </B2BButton>
    ));

  const renderDesktopView = () => (
    <Box className={clsx("experiences-availability-container", config.TENANT)}>
      {matchesMobile ? (
        <Box
          className={clsx("mobile-location-search-contents global-mobile-nav", {
            scrolled: scrollTrigger,
          })}
        >
          <MobileAvailabilitySearchControl
            calendarOpen={mobileCalendarOpen}
            onCalendarOpenChange={setMobileCalendarOpen}
          />
        </Box>
      ) : (
        <>
          {showCoreHomepage ? null : (
            <Header
              className="rewards-components-section"
              left={
                <Box className="rewards-account-section-left-content">
                  <Box className="logo" onClick={() => history.push(PATH_HOME)}>
                    {logo}
                  </Box>
                  <Box
                    className="rewards-account-section-travel-details"
                    data-testid="header-text-travel-details"
                  >
                    <Typography variant="body1" tabIndex={0}>
                      {isDateless
                        ? DATELESS_HEADER_TEXT(location ? location.label : "")
                        : VIEWING_EXPERIENCES_HEADER_TEXT(
                            location ? location.label : "",
                          )}
                    </Typography>
                    {headerDateText}
                  </Box>
                </Box>
              }
              right={
                <Box className="desktop-experiences-availability-rewards-account-contents">
                  <RewardsAccountSelection
                    className="b2b hide-balance-border"
                    popoverClassName="b2b"
                  />
                  <TravelWalletDrawer />
                </Box>
              }
            />
          )}
          <AvailabilitySearchControl
            history={history}
            scrolled={scrollTrigger}
          />
        </>
      )}
      <Box
        className={clsx("experiences-availability-quick-filter-container", {
          scrolled: scrollTrigger,
        })}
      >
        <Box
          className={clsx("experiences-availability-quick-filter", {
            scrolled: scrollTrigger,
          })}
        >
          {location && (
            <B2BButton
              key={location?.id.Id}
              aria-label={`Best of ${location.label}`}
              className={clsx("experiences-availability-quick-filter-button", {
                "category-selected": selectedGroupedExperience === undefined,
              })}
              variant="b2b-shop-filter"
              onClick={() => {
                setSelectedGroupedExperience(undefined);
                setFilterTags([]);
                setSortOption(ExperiencesAvailabilitySort.Recommended);
                setExperiencesToShow([]);
                // clear categoryTagId from URL
                const searchParams = new URLSearchParams(
                  history.location.search,
                );
                searchParams.delete(CATEGORY_TAG_ID_PARAM);
                setCategoryTagId(null);
                // Update URL without refresh
                history.replace({
                  pathname: history.location.pathname,
                  search: searchParams.toString(),
                });
              }}
            >
              <Box className="generic-dropdown-content-container">
                <Icon
                  className="location-quick-filter-icon"
                  name={IconName.CompassThinIcon}
                />
                <Box className="text">{`Best of ${location.label}`}</Box>
              </Box>
            </B2BButton>
          )}
          {shouldRenderPillSkeleton
            ? renderQuickFilterSkeleton()
            : availabilityFilterPills.map((pill) => (
                <B2BButton
                  key={`${pill.type}-${pill.tagCount.tag.id}`}
                  aria-label={pill.tagCount.tag.name}
                  className={clsx(
                    "experiences-availability-quick-filter-button",
                    {
                      "category-selected":
                        selectedGroupedExperience?.tag.id === pill.tagCount.tag.id,
                      "attraction-selected": pill.type === "Attraction",
                    },
                  )}
                  variant="b2b-shop-filter"
                  onClick={() => {
                    handleQuickFilterChange(pill.tagCount, "tag-pill", pill.type);
                  }}
                >
                  <Box className="generic-dropdown-content-container">
                    <Box className="text">{`${pill.tagCount.tag.name} (${getPillDisplayCount(pill)})`}</Box>
                  </Box>
                </B2BButton>
              ))}
        </Box>
      </Box>
      <Box className="experiences-availability-split-view">
        <Box className="experiences-split-components-section">
          {!matchesMobile && (
            <Box className="left-section">
              <AvailabilityFilter />
            </Box>
          )}
          <Box className={clsx("right-section", "most-popular-view")}>
            {isDateless && (
              <NotificationBanner
                className="dateless-info-banner"
                label={DATELESS_BANNER_TEXT}
                severity={BannerSeverity.INFO}
                icon={<Icon name={IconName.InfoCircle} />}
                cta={DATELESS_BANNER_CTA}
                onClick={() => {
                  trackEvent({
                    eventName: ACTIVITIES_DATELESS_SELECT_DATES_CLICKED,
                    properties: {
                      ...activitiesAvailabilityProperties.properties,
                    },
                    encryptedProperties:
                      activitiesAvailabilityProperties.encryptedProperties,
                  });
                  // Mobile: open calendar via lifted state
                  if (matchesMobile) {
                    setMobileCalendarOpen(true);
                    return;
                  }
                  // Desktop: scroll to and open the date input in the search bar
                  const desktopPicker = document.querySelector<HTMLElement>(DESKTOP_DATE_PICKER_SELECTOR);
                  if (desktopPicker) {
                    desktopPicker.scrollIntoView({ behavior: "smooth", block: "center" });
                    const button = desktopPicker.querySelector<HTMLElement>("button");
                    button?.click();
                  }
                }}
              />
            )}
            {selectedGroupedExperience
              ? renderFilteredRightSection()
              : shouldRenderSkeleton ||
                  (experiencesMap.size > 0 && !experiencesToShow?.length)
                ? renderMostPopularPageSkeleton()
                : renderMostPopularPageSection()}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <Box
        className={clsx("experiences-availability-root", {
          mobile: matchesMobile,
          "travel-app": isTravelApp,
          xmod: isXmodRedesign,
          "core-homepage": showCoreHomepage,
        })}
      >
        {renderDesktopView()}

        <B2BLoadingPopup
          open={initialCallInProgress}
          message={FETCH_AVAILABILITY_TEXT}
          secondaryMessage={getEarnMessageToDisplay(
            fetchRewardsAccountsCallState,
            largestValueAccount,
          )}
          image={CDN_ASSETS.capone.searchLoading.experiences}
          className="experiences-availability-loading-popup"
          popupSize={matchesMobile ? "mobile" : "desktop"}
          onClose={() => {
            travelAppInterface.sendEvent({
              name: TravelAppInterfaceEventName.SEARCH_LOADED,
              funnel: TravelAppInterfaceFunnelType.ACTIVITIES,
            });
          }}
          isXmod={isXmodRedesign}
        />

        <BadgeExcellenceModal
          isOpen={isBadgeExcellenceModalOpen}
          isMobile={matchesMobile}
          onClose={() => {
            setBadgeExcellenceModalOpen(false);
          }}
        />
        {matchesMobile && !initialCallInProgress && (
          <>
            <FloatingMenuPill
              items={[
                {
                  label: "Sort & filter",
                  onClick: () => setIsFilterModalOpen(true),
                  icon: IconName.Settings,
                },
              ]}
            />
            <MobileAvailabilityFilter
              isFilterModalOpen={isFilterModalOpen}
              displaySort={true}
              onClose={() => setIsFilterModalOpen(false)}
            />
          </>
        )}
      </Box>
      <AvailabilityErrorModal />
    </>
  );
};
